import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import type { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { environment } from 'environments/environment';
import { BYPASS_MSAL } from '@postal-bridge/shared/auth/interceptors/skip-public-urls-http.interceptor';
import { ApiLogStoreService } from '../logging/api-log-store.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { catchError, finalize, from, switchMap, tap, throwError } from 'rxjs';

function normalizeBaseUrl(v: unknown): string {
  return String(v ?? '').trim().replace(/\/+$/, '');
}

const isApi = (url: string) => {
  const apiBase = normalizeBaseUrl(environment.apiConfig.apiUrl);
  const odataBase = normalizeBaseUrl((environment.apiConfig as any).odataUrl);

  return (
    (apiBase && url.startsWith(apiBase)) ||
    (odataBase && url.startsWith(odataBase)) ||
    (!/^https?:\/\//i.test(url) && (url.startsWith('/api') || url.startsWith('/odata')))
  );
};

const tenantFromAuthority = (authority: string) => {
  try {
    return new URL(authority).hostname.split('.')[0] || 'postalbridge';
  } catch {
    return 'postalbridge';
  }
};

function uuidV4(): string {
  try {
    const c = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch {}

  const bytes = new Uint8Array(16);
  try {
    (globalThis as any).crypto?.getRandomValues?.(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function capStringBytes(input: string, maxBytes: number): string {
  if (!input) return '';
  if (maxBytes <= 0) return '';

  const enc = (globalThis as any).TextEncoder ? new TextEncoder() : null;
  if (!enc) return input.length > maxBytes ? input.slice(0, maxBytes) : input;

  const bytes = enc.encode(input);
  if (bytes.length <= maxBytes) return input;

  let lo = 0;
  let hi = input.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const b = enc.encode(input.slice(0, mid));
    if (b.length <= maxBytes) lo = mid;
    else hi = mid - 1;
  }
  return input.slice(0, lo);
}

export const debugInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (!environment.production && isApi(req.url)) {
    // eslint-disable-next-line no-console
    console.log('[HTTP] ->', req.method, req.url);
  }
  return next(req);
};

export const requestIdInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (!isApi(req.url)) return next(req);
  if (req.headers.has('X-Request-Id')) return next(req);
  return next(req.clone({ setHeaders: { 'X-Request-Id': uuidV4() } }));
};

let nativeClientHeadersPromise: Promise<Record<string, string>> | undefined;

function nativeClientHeaders(): Promise<Record<string, string>> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve({});
  return nativeClientHeadersPromise ??= App.getInfo()
    .then(info => ({
      'X-Client-App': 'customer',
      'X-Client-Platform': Capacitor.getPlatform(),
      'X-Client-Version': String(info.version || 'unknown'),
      'X-Client-Build': String(info.build || 'unknown'),
    }))
    .catch(() => ({}));
}

export const clientVersionInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (!isApi(req.url)) return next(req);
  return from(nativeClientHeaders()).pipe(switchMap(headers =>
    next(Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req),
  ));
};

export const apiLoggingInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (!isApi(req.url)) return next(req);

  const store = inject(ApiLogStoreService);

  const startedAt = (globalThis.performance?.now?.() ?? Date.now());
  const requestId = req.headers.get('X-Request-Id') || uuidV4();
  const url = store.redactUrl(req.url);

  let logged = false;

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        logged = true;
        const durationMs = Math.max(0, (globalThis.performance?.now?.() ?? Date.now()) - startedAt);
        void store.append({
          ts: new Date().toISOString(),
          requestId,
          method: req.method,
          url,
          status: event.status,
          ok: event.ok,
          durationMs: Math.round(durationMs),
        });
      }
    }),
    catchError((err: any) => {
      logged = true;
      const durationMs = Math.max(0, (globalThis.performance?.now?.() ?? Date.now()) - startedAt);

      const body = store.redactErrorBody(err?.error);
      const bodySnippet = body ? capStringBytes(body, 2048) : undefined;

      void store.append({
        ts: new Date().toISOString(),
        requestId,
        method: req.method,
        url,
        status: typeof err?.status === 'number' ? err.status : null,
        ok: false,
        durationMs: Math.round(durationMs),
        error: {
          message: typeof err?.message === 'string' ? err.message : undefined,
          bodySnippet,
        },
      });

      return throwError(() => err);
    }),
    finalize(() => {
      if (logged) return;
      const durationMs = Math.max(0, (globalThis.performance?.now?.() ?? Date.now()) - startedAt);
      void store.append({
        ts: new Date().toISOString(),
        requestId,
        method: req.method,
        url,
        status: null,
        ok: false,
        durationMs: Math.round(durationMs),
        error: {
          message: 'Request finished without HttpResponse',
        },
      });
    }),
  );
};

export const authInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (!isApi(req.url) || req.context.get(BYPASS_MSAL)) return next(req);

  const msal = inject(MsalService);
  const router = inject(Router);
  const scopes = environment.apiConfig.scopes;
  const baseAuthority = environment.msalConfig.auth.authority;
  const tenant = tenantFromAuthority(baseAuthority);

  const account: AccountInfo | undefined =
    msal.instance.getActiveAccount() || msal.instance.getAllAccounts()[0];

  if (!account) {
    return next(req);
  }

  const tfp = (account.idTokenClaims as any)?.tfp ?? (account.idTokenClaims as any)?.acr ?? null;
  const authority = tfp
    ? `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${tfp}`
    : baseAuthority;

  const acquire = (forceRefresh: boolean) =>
    from(
      msal.instance.acquireTokenSilent({
        account,
        scopes,
        authority,
        forceRefresh,
      } as SilentRequest),
    );

  const attach = (token: string) =>
    req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

  return acquire(false).pipe(
    catchError(() => acquire(true)),
    switchMap((res) => next(attach(res.accessToken)).pipe(
      catchError((err) => {
        if ((err?.status === 401 || err?.status === 403) && !req.headers.has('X-Auth-Retry')) {
          return acquire(true).pipe(
            switchMap((res2) =>
              next(
                attach(res2.accessToken).clone({
                  headers: attach(res2.accessToken).headers.set('X-Auth-Retry', '1'),
                }),
              ),
            ),
          );
        }

        if (err?.status === 401 || err?.status === 403) {
          // IMPORTANT: do NOT start an interactive login from an interceptor.
          // It commonly causes `interaction_in_progress` and makes auth flaky.
          try {
            if (router.url !== '/login' && router.url !== '/auth-redirect') {
              sessionStorage.setItem('pb.postLoginUrl', router.url || '/tabs/inbox');
            }
          } catch {
            /* ignore */
          }

          try {
            void router.navigateByUrl('/login', { replaceUrl: true });
          } catch {
            /* ignore */
          }
        }

        return throwError(() => err);
      }),
    )),
  );
};
